import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { UserEntity } from './user.entity';

/**
 * Audit trail for every change to the tenant's phone-system
 * configuration (Tier 1 — DD.16). Captures who changed what when
 * across the surfaces an admin can edit:
 *   - 'softphone_number'        — provisioning + recording flag
 *   - 'softphone_number_config' — destination, ring strategy, DND,
 *                                 fallback, voicemail greeting
 *   - 'softphone_group_config'  — per-Group ring strategy + fallback
 *   - 'softphone_role_config'   — per-Role ring strategy + fallback
 *   - 'tenant_setting'          — tenant-default ring strategy,
 *                                 hold music, default recording
 *
 * Before / after snapshots are JSON for forensic flexibility — the
 * exact column-level diff is reconstructible client-side. Cascade
 * actions (apply tenant default to all numbers) write a SINGLE row
 * with action='cascade' rather than one row per affected number to
 * keep the log readable; the affected count is inside `afterJson`.
 *
 * Append-only — no UPDATE / DELETE on this table once a row lands.
 * Read endpoint reverse-paginates for the admin display ("most
 * recent first").
 */
@Index('idx_softphone_audit_created', ['createdDate'], {})
@Index('idx_softphone_audit_entity', ['entityType', 'entityId'], {})
@Index('idx_softphone_audit_user', ['fkUserId'], {})
@Entity('softphone_config_audit')
export class SoftphoneConfigAuditEntity extends BaseEntity {
	@Column('int', {
		name: 'fk_user__id',
		unsigned: true,
		nullable: true,
	})
	public fkUserId!: number | null;

	@ManyToOne(() => UserEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
		nullable: true,
	})
	@JoinColumn([{ name: 'fk_user__id', referencedColumnName: 'id' }])
	public fkUser!: UserEntity | null;

	@Column('varchar', {
		name: 'entityType',
		length: 64,
	})
	public entityType!:
		| 'softphone_number'
		| 'softphone_number_config'
		| 'softphone_group_config'
		| 'softphone_role_config'
		| 'tenant_setting';

	/**
	 * The id of the affected row in the entity table — `null` for
	 * tenant-setting rows (which are keyed by setting key, not int
	 * id) and for cascade actions that touch many rows.
	 */
	@Column('int', {
		name: 'entityId',
		unsigned: true,
		nullable: true,
	})
	public entityId!: number | null;

	@Column('varchar', {
		name: 'action',
		length: 16,
	})
	public action!: 'create' | 'update' | 'delete' | 'cascade';

	/**
	 * Free-form label describing the change (e.g. "Set tenant default
	 * recording: ON", "Reassigned number +1248555... from User #42 to
	 * Group #7"). Renders as the primary line in the admin display so
	 * the admin doesn't need to diff JSON for the common case.
	 */
	@Column('varchar', {
		name: 'description',
		length: 255,
	})
	public description!: string;

	@Column('json', { name: 'beforeJson', nullable: true })
	public beforeJson!: Record<string, unknown> | null;

	@Column('json', { name: 'afterJson', nullable: true })
	public afterJson!: Record<string, unknown> | null;
}
